/**
 * Revenue Infograph Component
 *
 * Visual breakdown of revenue model:
 * Resident Spend → 30% Adoption → City & Treasury Splits
 *
 * Parameterized for any city - calculates values based on props
 */

import React from 'react';
import { Users, PiggyBank, Landmark } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

interface RevenueInfographProps {
  annualConsumerSpending: string; // e.g., "$6B" or "$200-300B"
  adoptionRate?: number; // default 0.30 (30%)
  cityRevenue: string; // e.g., "$3M-$5M+/yr"
  treasuryRevenue: string; // e.g., "$3M-$5M+/yr"
}

export function RevenueInfograph({
  annualConsumerSpending,
  adoptionRate = 0.3,
  cityRevenue,
  treasuryRevenue,
}: RevenueInfographProps) {
  // Calculate 30% adoption slice from annual spending
  const calculateAdoptionSlice = (spending: string, rate: number): string => {
    // Parse spending like "$6B", "$6–8B", "$200-300B", etc.
    const match = spending.match(/\$(\d+)(?:–|-)(\d+)?([BMK])?|(\d+)([BMK])?/i);
    if (!match) return spending;

    // Handle range format "$6–8B"
    if (match[1] && match[2]) {
      const low = parseFloat(match[1]) * rate;
      const high = parseFloat(match[2]) * rate;
      const unit = match[3] || 'B';
      return `$${low.toFixed(1)}–${high.toFixed(1)}${unit}`;
    }

    // Handle single value format "$6B"
    const value = parseFloat(match[1] || match[4]);
    const unit = match[3] || match[5] || 'B';
    const result = value * rate;

    return `$${result.toFixed(1)}${unit}`;
  };

  const adoptionSlice = calculateAdoptionSlice(annualConsumerSpending, adoptionRate);
  const adoptionPercent = Math.round(adoptionRate * 100);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
      {/* Resident Annual Spend */}
      <Card className="border-2 border-primary/20">
        <CardContent className="p-6 pt-5 flex flex-col items-center text-center">
          <Users className="w-10 h-10 text-primary mb-2" aria-hidden="true" />
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Resident Annual Spend
          </p>
          <p className="text-2xl font-bold text-primary mt-1">{annualConsumerSpending}</p>
          <p className="text-[10px] text-muted-foreground mt-1">Excludes industrial plants</p>
        </CardContent>
      </Card>

      {/* Adoption Slice */}
      <Card className="border-2 border-primary/20">
        <CardContent className="p-6 pt-5 flex flex-col items-center text-center">
          <PiggyBank className="w-10 h-10 text-primary mb-2" aria-hidden="true" />
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            {adoptionPercent}% Adoption Slice
          </p>
          <p className="text-2xl font-bold text-primary mt-1">{adoptionSlice}</p>
          <p className="text-[10px] text-muted-foreground mt-1">Flows through verified vendors</p>
        </CardContent>
      </Card>

      {/* Independent Splits */}
      <Card className="border-2 border-primary/20">
        <CardContent className="p-6 pt-5 flex flex-col items-center text-center">
          <Landmark className="w-10 h-10 text-primary mb-2" aria-hidden="true" />
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Independent Splits
          </p>
          <p className="text-2xl font-bold text-primary mt-1">City &amp; Treasury</p>
          <p className="text-[10px] text-muted-foreground mt-1">
            Each: {cityRevenue} @ {adoptionPercent}%
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
